'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { SubjectEntry } from '../applicationReducer';
import { FormErrorBox } from '../FormErrorBox';
import { validateSubjects } from '../validation';
import type { MessageKey } from '../validation';
import type { StepProps } from './stepProps';
import { StepShell } from './StepShell';
import {
  formSelectBaseClass,
  SelectChevron,
} from '@/components/form/SelectField';

/**
 * Subject options, per 10.03.04.03 AC 5.
 *
 * As in {@link DEGREE_OPTIONS}, `value` is submitted verbatim — the backend
 * sends `"Fach: Stufe"` to the NHL-Tool — so it stays German in every locale
 * and only `labelKey` is translated.
 */
export const SUBJECT_OPTIONS = [
  { value: 'Mathematik', labelKey: 'subjectMathematik' },
  { value: 'Deutsch', labelKey: 'subjectDeutsch' },
  { value: 'Englisch', labelKey: 'subjectEnglisch' },
  { value: 'Französisch', labelKey: 'subjectFranzoesisch' },
  { value: 'Latein', labelKey: 'subjectLatein' },
  { value: 'Spanisch', labelKey: 'subjectSpanisch' },
  { value: 'Physik', labelKey: 'subjectPhysik' },
  { value: 'Chemie', labelKey: 'subjectChemie' },
  { value: 'Biologie', labelKey: 'subjectBiologie' },
  { value: 'Wirtschaftslehre BWL VWL', labelKey: 'subjectWirtschaft' },
  { value: 'Rechnungswesen', labelKey: 'subjectRechnungswesen' },
] as const;

/** Teaching levels, per AC 5. Sent as `"Fach: Stufe"` by the backend. */
export const LEVEL_OPTIONS = [
  { value: 'Grundschule', labelKey: 'levelGrundschule' },
  { value: 'Bis 10. Klasse', labelKey: 'levelBis10' },
  { value: 'Bis 13. Klasse', labelKey: 'levelBis13' },
] as const;

const GROUP_COUNT = 3;

const emptyEntry = (): SubjectEntry => ({ subject: '', level: '' });

/**
 * Step 4 — Fächer (AC 5). Three groups, each a subject dropdown plus a row of
 * level radios. Only the first group is required.
 *
 * The group's name lives in the select's empty option ("Fach 1 *"), not in a
 * separate label — the same pattern as the Abschluss step, and what the design
 * shows.
 */
export const Step4Subjects = ({
  state,
  dispatch,
  chrome,
  onBack,
  onNext,
}: StepProps) => {
  const t = useTranslations('tutorApplication');
  const groups: SubjectEntry[] = Array.from(
    { length: GROUP_COUNT },
    (_, index) => state.subjects[index] ?? emptyEntry(),
  );

  const [errorKey, setErrorKey] = useState<MessageKey | undefined>(undefined);

  const handleNext = () => {
    const problem = validateSubjects(state);
    setErrorKey(problem);
    if (!problem) {
      onNext();
    }
  };

  const update = (index: number, patch: Partial<SubjectEntry>) => {
    const next = groups.map((entry, i) =>
      i === index ? { ...entry, ...patch } : entry,
    );
    dispatch({ type: 'UPDATE_SUBJECTS', subjects: next });
    setErrorKey(undefined);
  };

  return (
    <StepShell
      heading={t('step4Heading')}
      chrome={chrome}
      onBack={onBack}
      onNext={handleNext}
      hasError={Boolean(errorKey)}
    >
      {errorKey && <FormErrorBox>{t(errorKey)}</FormErrorBox>}

      {/* The question covers all three groups, so it is asked once above them
          rather than repeated per group. Each group keeps its own accessible
          name via the select's `aria-label` ("Fach 1"…). */}
      <p className="text-base">
        {t('subjectsQuestion')}
        {' *'}
      </p>

      {groups.map((entry, index) => {
        const required = index === 0;
        const groupLabel = t('subjectGroupLabel', { index: index + 1 });

        return (
          <fieldset key={index} className="flex flex-col gap-4">
            <legend className="sr-only">{groupLabel}</legend>

            {/*
              The shared class, as in Step 3 — this select was hand-rolled too,
              and so was still at 14px and zooming on iOS (SH-480). It also
              carried `appearance-none` with no chevron of its own, so it had no
              dropdown affordance at all; the shared pair supplies one.
            */}
            <div className="relative">
              <select
                aria-label={groupLabel}
                aria-invalid={required && Boolean(errorKey)}
                className={classNames(
                  formSelectBaseClass,
                  'pr-10',
                  required && errorKey ? 'border-red-400' : 'border-[#eae8e7]',
                )}
                value={entry.subject}
                onChange={(e) => update(index, { subject: e.target.value })}
              >
                <option value="">{`${groupLabel}${required ? ' *' : ''}`}</option>
                {SUBJECT_OPTIONS.map((subject) => (
                  <option key={subject.value} value={subject.value}>
                    {t(subject.labelKey)}
                  </option>
                ))}
              </select>
              <SelectChevron />
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {LEVEL_OPTIONS.map((level) => (
                <label key={level.value} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`level-${index}`}
                    value={level.value}
                    checked={entry.level === level.value}
                    onChange={() => update(index, { level: level.value })}
                  />
                  <span className="text-base">{t(level.labelKey)}</span>
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}

      <p className="text-base">{t('subjectsMore')}</p>
    </StepShell>
  );
};
